import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { TelemetryNotesList } from "@/components/telemetry/TelemetryNoteMarkers";
import type { TelemetryNote } from "@/types";

interface NotesSidePanelProps {
  notes: TelemetryNote[];
  selectedNoteId?: string | null;
  notesEnabled: boolean;
  onNotesEnabledChange: (enabled: boolean) => void;
  onNoteClick: (note: TelemetryNote) => void;
  onTimeSeek: (time: number) => void;
  onClose: () => void;
}

/**
 * Desktop-only notes side panel with a click-away backdrop. Rendered when
 * the notes toggle is active and the layout is not mobile.
 */
export default function NotesSidePanel({
  notes,
  selectedNoteId,
  notesEnabled,
  onNotesEnabledChange,
  onNoteClick,
  onTimeSeek,
  onClose,
}: NotesSidePanelProps) {
  const { t } = useTranslation(["pages", "common"]);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-25 z-40"
        onClick={onClose}
      />

      {/* Notes Panel */}
      <div className="fixed top-0 right-0 h-full w-96 bg-[#0d0d14] shadow-2xl border-l border-[#1a1a2e] z-50 transform transition-transform duration-300">
        <div className="p-4 border-b border-[#1a1a2e] bg-[#111827]">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-[#e2e8f0]">
              {t("pages:sessions.analytics.telemetryNotes")}
            </h3>
            <div className="flex items-center gap-3">
              <label
                className="flex items-center gap-2 text-xs text-[#6b7280] cursor-pointer select-none"
              >
                <input
                  type="checkbox"
                  checked={notesEnabled}
                  onChange={(event) =>
                    onNotesEnabledChange(event.target.checked)
                  }
                  className="accent-[#7c3aed]"
                />
                {t("pages:sessions.analytics.clickTimelineToAdd")}
              </label>
              <button
                onClick={onClose}
                className="p-1 text-[#4b5563] hover:text-[#94a3b8] hover:bg-[#1f2937] rounded transition-colors"
                title={t("pages:sessions.analytics.closeNotesPanel")}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
        <div
          className="p-4 overflow-y-auto"
          style={{ height: "calc(100% - 73px)" }}
        >
          <TelemetryNotesList
            notes={notes}
            onNoteClick={onNoteClick}
            onTimeSeek={onTimeSeek}
            selectedNoteId={selectedNoteId}
          />
        </div>
      </div>
    </>
  );
}
